import {
  CatchBoundary,
  createFileRoute,
  Outlet,
  redirect,
  useLocation,
} from '@tanstack/react-router'
import { AppShell } from '@/components/AppShell'
import { RouteError } from '@/components/RouteError'
import { leaveForLegacy } from '@/lib/legacy-app'
import { accountsQuery, sessionQuery } from '@/lib/queries'

/**
 * The authenticated application lives under /app; everything outside it (the landing page)
 * renders without the shell.
 *
 * The gate resolves in the **loader**, not in render. With AuthKit the session lives in an
 * HttpOnly cookie, so the only way to learn about it is to ask the server — and doing that in the
 * loader means the route either renders signed-in or never mounts at all. The Clerk version had to
 * render a splash while the client SDK booted, then swap; there is nothing to wait for here
 * because the answer arrives before the first paint of this route.
 *
 * It is still a UX courtesy, not the security boundary. Every /api route that matters is behind
 * `workosAuth` cookie verification in apps/api, which a route guard cannot substitute for — those
 * endpoints are reachable directly, without this app.
 */
export const Route = createFileRoute('/app')({
  loader: async ({ context }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery)
    leaveForLegacy(session)
    if (!session.user) {
      // `throw redirect` rather than <Navigate>: it aborts the loader chain, so child loaders
      // never fire authenticated requests we already know will 401.
      throw redirect({ to: '/' })
    }
    // The account list drives the sidebar switcher; prefetched here so it resolves without a
    // waterfall on the first authenticated paint.
    await context.queryClient.ensureQueryData(accountsQuery)
    // Return the narrowed user, not `session` — the guard narrows the property, not the object,
    // so passing `session` on would make every consumer re-handle a null that can't happen.
    return { user: session.user }
  },
  component: AppLayout,
})

function AppLayout() {
  const { user } = Route.useLoaderData()
  const location = useLocation()

  return (
    <AppShell user={user}>
      {/* A page that throws while rendering keeps the shell around it: the sidebar is how you
          leave. Resets on navigation, so the next page is not stuck showing the last one's error. */}
      <CatchBoundary getResetKey={() => location.pathname} errorComponent={RouteError}>
        <Outlet />
      </CatchBoundary>
    </AppShell>
  )
}
